import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Currency, EExpenseTypeKind, ExpenseType, Language, NotificationTypes, ScheduleAdjustmentRequestStates, SynchronizationCalendar, TId, Timetables, User, Worker, WorkerExpense, WorkerFloatingHoliday } from 'src/app/models';
import { Subject, firstValueFrom, tap } from 'rxjs';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import { UserChangePasswordComponent } from '../user-change-password/user-change-password.component';
import { AdminLanguagesAddComponent } from '../../../libs/components/admin/admin-languages-add/admin-languages-add.component';
import { AdminLanguagesEditComponent } from '../../../libs/components/admin/admin-languages-edit/admin-languages-edit.component';
import { OptionChangedEvent } from 'devextreme/ui/tab_panel';
import { EditorPreparingEvent, InitializedEvent } from 'devextreme/ui/data_grid';
import { GenericReadOnlyResource, GenericResource } from 'src/app/resources';
import { ProfileScheduleAdjustmentRequestsAddComponent } from './components/profile-schedule-adjustment-requests-add/profile-schedule-adjustment-requests-add.component';
import { ProfileScheduleAdjustmentRequests } from './profile-schedule-adjustment-request-resource';
import { ProfileWorkerExpensesAddComponent } from './components/profile-worker-expenses-add/profile-worker-expenses-add.component';
import { formatCurrency } from '@angular/common';


@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.sass'],
    standalone: false
})
export class UserProfileComponent extends GenericCrudEditComponent<User>{

  languages: Language[] = [];
  roles: any;
  uploaderElement: Element | undefined;
  selectedImage: File | undefined;
  newPassword: string;
  notificationTypes = NotificationTypes.map(x => ({
    id: x.id,
    name: this.translationService.instant(`enumeration.NotificationTypes.${x.name}`)
  }));
  synchronizationCalendars: SynchronizationCalendar[];

  isAdmin: boolean = false;
  imgBusy: boolean;

  calendarName: string;

  showResetPassword: boolean = false;

  createLanguageComponent = AdminLanguagesAddComponent;
  editLanguageComponent = AdminLanguagesEditComponent;
  changePasswordComponent = UserChangePasswordComponent;

  busy: boolean;
  processingText: string = this.translationService.instant("common.processing");

  worker?: Worker;
  timeTables: Timetables[] = [];
  openedTabItems: string[] = [];

  floatingHolidaysRes?: GenericReadOnlyResource<WorkerFloatingHoliday>;
  scheduleAdjustmentRequestsRes?: ProfileScheduleAdjustmentRequests;
  workerExpensesRes?: GenericResource<WorkerExpense>;

  addRequestComponent = ProfileScheduleAdjustmentRequestsAddComponent;
  addWorkerExpenseComponent = ProfileWorkerExpensesAddComponent;

  expenseTypes: ExpenseType[] = [];

  stateValues = ScheduleAdjustmentRequestStates;

  currency: Currency | undefined;
  formatCurrency = formatCurrency;

  constructor() {
    super();
    this.resource = this.api.resources.auth.users;
    const editorActions = [];
    editorActions.push({
      name: 'changePassword',
      icon: 'key',
      action: async () => this.changePasswordButtonClicked(null),
    });
    this.editorActions.next(editorActions);
    this.newPassword = '';
    this.selectedImage = undefined;
    this.imgBusy = false;
    this.destroy$ = new Subject();
    this.busy = false;

    this.synchronizationCalendars = [];
    this.calendarName = '';

    this.storage.getLanguages().subscribe(languages => this.languages = languages);
    this.currency = this.storage.companyCurrency();
  }

  initFormGroup(): FormGroup {
    const fg = this.fb.group({
      userName: [this.entity!.userName],
      //password: [this.entity!.password, [Validators.required]],
      email: [this.entity!.email],
      languageId: [this.entity!.languageId],
      roleId: [this.entity!.roleId],
      name: [this.entity!.name],
      surname: [this.entity!.surname],
      phoneNumber: [this.entity!.phoneNumber],
      notificationTypeId: [this.entity!.notificationTypeId],
      synchronizationCalendarId: [this.entity!.synchronizationCalendarId],
      synchronizationCalendarName: [this.entity!.synchronizationCalendarName],
    });
    return fg;
  }

  displayEntity(): string {
    return this.translationService.instant('common.profile');
  }

  protected override async preFetch(): Promise<void> {
   if (this.api.auth.userValue?.workerId) {
      this.worker = await firstValueFrom(this.api.resources.workers.get(this.api.auth.userValue!.workerId));
      this.workerExpensesRes = this.api.resources.operations.workerExpensesMe;
      this.scheduleAdjustmentRequestsRes = new ProfileScheduleAdjustmentRequests(this.api)
      .asMappedResource(
        {
          to: data => {
          data.companyId = this.api.auth.userValue!.lastCompanyId;
          return data;
        }
        }
      ) as ProfileScheduleAdjustmentRequests;
      const expenseTypes = await firstValueFrom(this.api.resources.operations.expenseTypes.getAll());
      this.expenseTypes = expenseTypes.filter(x => x.type === EExpenseTypeKind.WorkerExpense);
      this.floatingHolidaysRes = new GenericReadOnlyResource<WorkerFloatingHoliday>(this.api, 'floatingHolidays', 'workerFloatingHoliday', {ctx: `Workers/${this.api.auth.userValue!.workerId}`})
      .asMappedResource(
      {
        from: async (exerciseHolidays: WorkerFloatingHoliday) => {
          exerciseHolidays.editedDays = exerciseHolidays.editedDays === null ? exerciseHolidays.days : exerciseHolidays.editedDays;
          return exerciseHolidays;
        }
      }) as GenericReadOnlyResource<WorkerFloatingHoliday>;
    } 
  }

  protected override onInitExtendActions(): void {

    this.fillCombos();
    this.fillUser();

    this.uploaderElement = document.getElementById('image-uploader') as Element;
    this.isAdmin = this.acl.isAdmin();

    this.calendarName = this.entity!.synchronizationCalendarName;
  }

  async fillCombos() {
    this.languages = (await firstValueFrom(this.storage.getLanguages()))?.filter(x => x.systemInterfaceLanguage);
    this.api.resources.auth.roles.getAll().subscribe(roles => this.roles = roles);

    this.fillSynchronizationCalendarsCombo();
  }

  fillSynchronizationCalendarsCombo() {
    if (this.entity?.synchronizationCalendarId !== undefined && this.entity.synchronizationCalendarId !== '' &&
    this.entity.synchronizationCalendarName !== undefined && this.entity.synchronizationCalendarName !== '') {
      this.synchronizationCalendars.unshift({
        calendarId: this.entity.synchronizationCalendarId,
        calendarName: this.entity.synchronizationCalendarName
      });
    }
    this.api.getUserSynchronizationCalendars(this.api.auth.userValue?.email ?? '').subscribe(calendars => {
      this.synchronizationCalendars = calendars
      this.synchronizationCalendars.unshift({ calendarId: null, calendarName: this.translationService.instant('common.none')});
    });
    if (this.synchronizationCalendars === undefined) {
      this.synchronizationCalendars = [];
    }

    if (this.synchronizationCalendars.length > 0 && this.synchronizationCalendars[0].calendarId !== '0') {
      this.synchronizationCalendars.unshift({ calendarId: null, calendarName: this.translationService.instant('common.none')});
    }
  }

  fillUser() {
    if (this.api.auth.userValue?.userName){
      //TODO: si authService.userValue no té valor, tornar a pantalla de login
      this.api.resources.auth.users.get(this.api.auth.userValue!.userName!).subscribe(user => this.entity = user);
    }
  }

  override async updateEntity(): Promise<void> {
    const formfileds: any = await this.mapFormEntity();
    formfileds.synchronizationCalendarName = this.synchronizationCalendars.find(x => x.calendarId === formfileds?.synchronizationCalendarId)?.calendarName;
    let updated: any = {};
    const entityToUpdate: User = {
      ...this.entity,        // full original user
      ...formfileds          // override only fields from form
    };
    updated = await this.updateUser(entityToUpdate);
    if (updated)
    {
      this.form?.markAsPristine();
      this.entityChanged.emit(entityToUpdate);
      this.checkLanguageChange();
    }
  }

  async updateUser(data: User): Promise<boolean> {
    //TODO: com saber si han canviar les dades?

    try{
      const rupdate = await firstValueFrom(this.api.resources.auth.users.put(data));
      localStorage.setItem('user', data.userName);
      this.api.auth.setAndUpdateUser(rupdate);

      if (data.imageUrl !== undefined && data.imageUrl !== null && this.selectedImage !== undefined) {
        await firstValueFrom(this.api.updateUserImage(data,this.selectedImage));
      }

      return true;
    }
    catch (error){
      this.alertSrv.error(error);
      return false;
    } 
  }

  loadImagePreview(image: File | undefined) {
    if (image === undefined)
      return;

    this.selectedImage = image;

    const reader = new FileReader();
    const that = this;
    reader.onload = function (e) {
      that.entity!.imageUrl = e.target!.result as string;
    }
    reader.readAsDataURL(image);
  }

  async onFileUploaderChanged(e: any): Promise<void> {
    this.selectedImage = e.target.files[0];

    if (this.selectedImage !== undefined ) {
      const reader = new FileReader();
      reader.onload = async () => {
        this.entity!.imageUrl = reader.result as string;
        const rq = this.api.updateUserImage(this.entity!, this.selectedImage!).pipe(tap((rs) => {
          this.entity!.imageUrl = rs.imageUrl;
          this.api.auth.userValue!.imageUrl = rs.imageUrl;
        }));
        this.imgBusy = true;
        try {
          await firstValueFrom(rq);
        } catch (error) {
          this.entity!.imageUrl = '';
        } finally {
          this.imgBusy = false;
        }
      };
      reader.readAsDataURL(this.selectedImage);
    }
  }


  async deleteImage() : Promise<void>
  {
    if (this.entity!.imageUrl)
    {
      this.imgBusy = true;
      try{
        const rs = await firstValueFrom(this.api.deleteUserImage(this.entity!.userName));
        if (rs) {
          this.entity!.imageUrl = rs.imageUrl;
          this.api.auth.userValue!.imageUrl = rs.imageUrl;
        }
      } catch (error) {
        this.alertSrv.error(this.translationService.instant('common.errorDeletingImage'))
      } finally {
        this.imgBusy = false;
      }
    }

  }

  changePasswordButtonClicked(event: any) {
    this.showResetPassword = true;
  }

  newLanguageId:TId|null = null;
  onLanguageValueChanged = (event:any) => this.newLanguageId = event.value;

  private async checkLanguageChange() {
    if (this.newLanguageId) {
      const newLanguage:Language|undefined = this.languages.find(_ => _.id === this.newLanguageId);

      if (newLanguage && this.api.auth.userValue?.languageCode !== newLanguage.code) {
        //this.langService.code = newLanguage.code; // don't work!!
        location.reload();
      }
    }
  }

  onTabChanged($event: OptionChangedEvent) {
        if ($event.name === 'selectedItemKeys' && $event.value?.length > 0) {
          const item = $event.value[0].title;
          setTimeout(() => {
            if(!this.openedTabItems.includes(item)) {
              this.openedTabItems.push(item);
            }
          }, 1);
      }
    }

  setStateValue(row: any) {
    row.setValue(row.value);
  }

  // // Keeps 'amount' in sync while editing: DevExtreme calls this instead of its default
  // // assignment when 'quantity' changes, so we can also recompute the derived field.
  // setWorkerExpenseQuantity = (newData: Partial<WorkerExpense>, value: number, currentRowData: WorkerExpense) => {
  //   newData.quantity = value;
  //   newData.amount = value * currentRowData.unitPrice;
  // }

  // // Same idea for 'unitPrice': it's only editable when the selected expense type doesn't
  // // impose a fixed one (see onWorkerExpensesEditorPreparing), so keep 'amount' consistent
  // // when the worker overrides it.
  // setWorkerExpenseUnitPrice = (newData: Partial<WorkerExpense>, value: number, currentRowData: WorkerExpense) => {
  //   newData.unitPrice = value;
  //   newData.amount = (newData.quantity ?? currentRowData.quantity) * value;
  // }

  // When the expense type changes, adopt its fixed unit price if it has one; otherwise leave
  // the row's current unitPrice untouched (allowEditing is decided per-row, see below).
  setWorkerExpenseType = (newData: Partial<WorkerExpense>, value: TId, currentRowData: WorkerExpense) => {
    newData.expenseTypeId = value;
    const expenseType = this.expenseTypes.find(x => x.id === value);
    if (expenseType?.unitAmount !== null && expenseType?.unitAmount !== undefined) {
      newData.unitPrice = expenseType.unitAmount;
      newData.amount = (newData.quantity ?? currentRowData.quantity) * expenseType.unitAmount;
    }
  }

  // 'unitPrice' is only free-text when the row's expense type has no fixed unitAmount;
  // otherwise it's locked to that value (kept in sync by the setCellValue handlers above).
  onWorkerExpensesEditorPreparing(e: EditorPreparingEvent<WorkerExpense>) {
    if (e.parentType === 'dataRow' && e.dataField === 'unitPrice') {
      const expenseType = this.expenseTypes.find(x => x.id === e.row?.data.expenseTypeId);
      if (expenseType?.unitAmount !== null && expenseType?.unitAmount !== undefined) {
        e.editorOptions.disabled = true;
      }
    }
  }

  onWorkerExpensesGridInitialized(e: InitializedEvent) {
    // Editing a production-ticket-backed expense PUTs to ProductionExpenseTickets and the
    // backend updates the WorkerExpense asynchronously. 'repaint' keeps the value returned
    // by resource.put() instead of reloading the still-stale read model right after saving.
    e.component?.option('editing.refreshMode', 'repaint');
  }

}